<template>
  <v-container fluid>
    <v-row
      align="center"
      justify="center"
    >
      <v-col
        cols="12"
        md="10"
        sm="10"
      >
        <v-form
          ref="form"
          @submit.prevent
        >
          <v-row
            align="center"
            justify="center"
          >
            <!-- SEARCH FIELD -->
            <v-col cols="10">
              <v-text-field
                v-model="searchQuery"
                label="Search Case Records and Reports"
                @input="handleInput"
                hide-details
                @click="onClickInside"
              >
                <template v-slot:append>
                  <v-progress-circular
                    v-if="isTyping || isLoading"
                    indeterminate
                    color="primary"
                    size="24"
                    width="2"
                  ></v-progress-circular>
                </template>
                <template v-slot:prepend>
                  <v-icon>mdi-magnify</v-icon>
                </template>
              </v-text-field>

              <!-- RESULTS -->
              <v-slide-y-transition>
                <span v-if="noResults && showResults">
                  <!-- No results -->
                  <v-card
                    elevation="2"
                    class="text-center ml-8 pa-4 mt-1">
                    <v-icon
                      color="grey" large
                      >mdi-magnify</v-icon>
                    
                    <p class="text-h6 mt-4">No search results found</p>
                    <p class="text-body-2 grey--text">Please try again with a different search query. You may search by the case name or ID. </p>
                  </v-card>
                </span>
                
                <span v-if="showResults && (searchResults.cases?.length > 0 || searchResults.reports?.length > 0)">
                  <v-list
                    dense
                    class="ml-8 pa-0 mt-1"
                    elevation="2"
                    v-click-outside="onClickOutside"
                  >
                    <v-list-item-group>
                      <!-- CASES -->
                      <template v-if="searchResults.cases?.length > 0">
                        <v-subheader>Cases</v-subheader>
                        <v-list-item
                          v-for="(aCase, index) in searchResults.cases"
                          :key="`c-${index}`"
                          @click="routeToItem(aCase)"
                        >
                          <v-list-item-content>
                            <v-row
                              no-gutters
                              style="line-height: 0.5"
                            >
                              <v-col>
                                <TypeIcon
                                  :type="aCase.caseType"
                                  :includeText="false"
                                  class="mr-1"
                                />
                                <span class="font-weight-medium">{{ aCase.name }}</span>
                                <span
                                  class="text-caption ml-2 mt-0 pt-0"
                                  v-if="aCase.caseGroupId"
                                >
                                  {{ getCaseGroupName(aCase.caseGroupId) }}
                                </span>
                                <br />
                                <span
                                  v-if="aCase.localId"
                                  class="text-caption ml-1"
                                  >ID: {{ aCase.localId }}</span
                                >
                              </v-col>
                            </v-row>
                          </v-list-item-content>
                        </v-list-item>
                      </template>

                      <!-- REPORTS -->
                      <template v-if="searchResults.reports?.length > 0">
                        <v-subheader>Reports</v-subheader>
                        <v-list-item
                          v-for="(aCase, index) in searchResults.reports"
                          :key="`r-${index}`"
                          @click="routeToItem(aCase)"
                        >
                          <v-list-item-content>
                            <v-row
                              no-gutters
                              style="line-height: 0.5"
                            >
                              <v-col>
                                <TypeIcon
                                  :type="aCase.caseType"
                                  :includeText="false"
                                  class="mr-1"
                                />
                                <span class="font-weight-medium">{{ aCase.name }}</span>
                                <span
                                  class="text-caption ml-2 mt-0 pt-0"
                                  v-if="aCase.caseGroupId"
                                >
                                  {{ getCaseGroupName(aCase.caseGroupId) }}
                                </span>
                                <span class="text-body-2 font-weight-medium ml-2">
                                  <ResolutionStatusChips :resolutionStatus="aCase.status" />
                                </span>
                                <br />
                                <span
                                  v-if="aCase.localId"
                                  class="text-caption ml-1"
                                  >ID: {{ aCase.localId }}</span
                                >
                              </v-col>
                            </v-row>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-list-item-group>
                  </v-list>
                </span>
              </v-slide-y-transition>
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";
import { mapActions } from "vuex";
import TypeIcon from "../components/TypeIcon.vue";
import ResolutionStatusChips from "../components/ResolutionStatusChips";
import { getCaseGroupName } from "../utils/caseGroupUtils";

export default {
  name: "Search",
  components: { TypeIcon, ResolutionStatusChips },
  data() {
    return {
      searchQuery: "",
      isTyping: false,
      isLoading: false,
      showResults: true,
      noResults: false,
      typingTimeout: null,
      searchResults: [],
      debounceTimeout: null,
      prevQueryLength: 0,
    };
  },
  watch: {},
  computed: {
  },
  methods: {
    ...mapActions(["setGlobalAlert"]),
    getCaseGroupName,
    onClickOutside() {
      this.showResults = false;
      this.noResults = false;
    },
    onClickInside() {
      this.showResults = true;
    },
    handleInput() {
      this.noResults = false; // Immedietly clear no results message
      // Typing detection logic
      this.isTyping = true;
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
      }
      this.typingTimeout = setTimeout(() => {
        this.isTyping = false;
      }, 501); // 500ms inactivity threshold

      // Determine if this input change is a deletion
      const currentLength = this.searchQuery.length;
      const isDeleting = currentLength < this.prevQueryLength;
      this.prevQueryLength = currentLength;

      // Debounce the search API call (e.g., 300ms delay after last input)
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }

      if (!this.searchQuery.trim()) {
        this.searchResults = [];
      } else if (!isDeleting) {
        this.debounceTimeout = setTimeout(() => {
          if (this.searchQuery.length > 2) {
            this.performSearch(this.searchQuery);
          }
        }, 500);
      }
    },
    async performSearch(q) {
      console.log("API called: " + q);
      this.isLoading = true;
      this.showResults = true;
      try {
        const response = await axios.get("/api/web/search", { params: { q } });
        // console.log("Results: " + JSON.stringify(response.data));
        // Separate into sub lists for Cases and Reports
        if (response.data?.length > 0) {
          this.searchResults = this.separateByType(response.data);
        } else {
          // Show no results
          this.searchResults = []
          this.noResults = true;
        }
      } catch (error) {
        console.error("Search error:", error);
        this.searchResults = [];
      } finally {
        this.isLoading = false;
      }
    },
    routeToItem(item) {
      // console.log("STEP 5: Routing to " + JSON.stringify(item))
      if (item.type === "CASE") {
        this.$router
          .push({
            path: `/casemanager/${item.id}`,
            params: { id: item.id },
          })
          .finally((this.isLoading = false));
      } else if (item.type === "REPORT") {
        this.$router
          .push({
            path: `/resolution/${item.id}`,
            params: { id: item.id },
          })
          .finally((this.isLoading = false));
      }
      // this.searchResults = [];
    },
    /**
     * Separates items from the given JSON array into two lists based on the 'type' property.
     * Assumes the JSON is an array of objects, each with a 'type' field that is either 'CASE' or 'REPORT'.
     */
    separateByType(jsonArray) {
      const cases = [];
      const reports = [];

      jsonArray.forEach((item) => {
        if (item.type === "CASE") {
          cases.push(item);
        } else if (item.type === "REPORT") {
          reports.push(item);
        }
        // Items with other types are ignored
      });

      return { cases, reports };
    },
  },
};
</script>

<style scoped>
/* Fixes unaligned headers */
.v-data-table-header th {
  white-space: nowrap;
}

#keep .v-navigation-drawer__border {
  display: none;
}
</style>
